import { Box } from '@mui/material';
import { useEffect, useLayoutEffect, useState, useRef, useMemo } from 'react';
import RepertoireItem from './RepertoireItem';
import { useSelector, useDispatch } from 'react-redux';
import { debounce } from 'lodash';
import { RepertoireReducer, RepertoireData } from 'Types/RepertoireTypes';
import { MainAppReducer } from 'Types';
// import EmptyRepertoire from 'assets/images/repertoire/EmptyRepertoire.png';
import EmptyRepertoire from '../../../src/assets/images/Repertoire/EmptyRepertoire.png';
import { REPERTOIRE_PROGRESS } from 'Types/RepertoireTypes';
import { DragScroll } from './DragScroll';
import { Menu } from 'Components/RoadmapComponents/Menu';
import PageNav from './PageNav';
import * as repertoireActions from '../../Actions/repertoire';

const Home = () => {
  const dispatch = useDispatch();
  const appReducerData = useSelector((state: MainAppReducer) => state.mainAppReducer);
  const [recommendedRep, setRecommendedRep] = useState(true);
  const [menuOpen, setMenuOpen] = useState(false);
  const highestLevelCompleted = appReducerData.highestLevelCompleted;

  const repertoireReducerData = useSelector((state: RepertoireReducer) => state.repertoireReducer);
  const repertoireData = repertoireReducerData?.repertoireData;
  const userRepertoireProgress = repertoireReducerData?.userRepertoireProgresses;
  const verticalScrollPosition = repertoireReducerData?.verticalScrollPositions?.home ?? 0;

  const containerRef = useRef<HTMLDivElement | null>(null);
  const hasRestoredScroll = useRef(false);

  // Debounced save of vertical scroll position to Redux (only fires 150ms after scrolling stops)
  const debouncedSaveVerticalScroll = useMemo(
    () =>
      debounce((position: number) => {
        dispatch(repertoireActions.setVerticalScroll({ page: 'home', position }));
      }, 150),
    [dispatch],
  );

  // Flush pending scroll position save on unmount (ensures position is saved before navigating away)
  useEffect(() => {
    return () => debouncedSaveVerticalScroll.flush();
  }, [debouncedSaveVerticalScroll]);

  // Restore vertical scroll position on mount (useLayoutEffect to prevent flash)
  useLayoutEffect(() => {
    if (containerRef.current && !hasRestoredScroll.current && verticalScrollPosition > 0) {
      containerRef.current.scrollTop = verticalScrollPosition;
      hasRestoredScroll.current = true;
    }
  }, [verticalScrollPosition]);

  // Add scroll listener for vertical scroll
  useEffect(() => {
    const container = containerRef.current;
    if (container) {
      const handleVerticalScroll = () => {
        debouncedSaveVerticalScroll(container.scrollTop);
      };
      container.addEventListener('scroll', handleVerticalScroll);
      return () => container.removeEventListener('scroll', handleVerticalScroll);
    }
  }, [debouncedSaveVerticalScroll]);

  const { recentlyPlayedRep, atYourLevelRep, morePracticeRep, inProgressRep } = useMemo(() => {
    const daysAgo = new Date();
    daysAgo.setDate(daysAgo.getDate() - 7);
    const recently: RepertoireData[] = [];
    const atLevel: RepertoireData[] = [];
    const more: RepertoireData[] = [];
    const inProgress: RepertoireData[] = [];
    repertoireData?.forEach((rep) => {
      const urp = userRepertoireProgress?.find((item) => item.repertoire?.id === rep?.id);
      if (urp && new Date(urp.last_played_at) > daysAgo) {
        recently.push(rep);
      }
      if (urp && urp.status == REPERTOIRE_PROGRESS.IN_PROGRESS) {
        inProgress.push(rep);
      }
      if (rep.level?.level_number == highestLevelCompleted) {
        atLevel.push(rep);
      }
      if (
        rep.level?.level_number < highestLevelCompleted &&
        rep.level?.level_number > highestLevelCompleted - 5
      ) {
        more.push(rep);
      }
    });
    const sortDescendingByLevel = (a: RepertoireData, b: RepertoireData) =>
      (b.level?.level_number || 0) - (a.level?.level_number || 0);
    recently.sort(sortDescendingByLevel);
    atLevel.sort(sortDescendingByLevel);
    more.sort(sortDescendingByLevel);
    inProgress.sort(sortDescendingByLevel);
    return {
      recentlyPlayedRep: recently,
      atYourLevelRep: atLevel,
      morePracticeRep: more,
      inProgressRep: inProgress,
    };
  }, [repertoireData, userRepertoireProgress, highestLevelCompleted]);

  useEffect(() => {
    if (
      recentlyPlayedRep.length == 0 &&
      atYourLevelRep.length == 0 &&
      inProgressRep.length == 0 &&
      morePracticeRep.length == 0
    ) {
      setRecommendedRep(false);
    } else {
      setRecommendedRep(true);
    }
  }, [recentlyPlayedRep, atYourLevelRep, inProgressRep, morePracticeRep]);

  return (
    <Box
      sx={{
        display: 'flex',
        height: '100vh',
        width: '100vw',
      }}
    >
      <Menu
        selected={'Repertoire'}
        mobileOpen={menuOpen}
        setMobileOpen={setMenuOpen}
        hideMobileTrigger
      />
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: 'calc(100% - var(--app-sidebar-width, 100px))',
        }}
      >
        <PageNav selected={'Home'} onMenuOpen={() => setMenuOpen(true)} />
        {/* <Box sx={{margin: '0px 0px', justifyContent: 'flex-start', height: 'calc(100vh - 60px)', overflowY: 'auto'}} ref={containerRef}> */}
        <Box
          sx={{
            margin: '0px 0px',
            justifyContent: 'flex-start',
            height: 'calc(100vh - var(--rep-nav-height, 100px))',
            overflowY: 'auto',
          }}
          ref={containerRef}
        >
          <Box sx={{ ...recForYouStyles, ...recTextStyles }}>Recommended For You</Box>

          {recommendedRep ? (
            <Box sx={{ margin: '0px 28px', paddingTop: '20px' }}>
              {recentlyPlayedRep.length > 0 && (
                <RecentlyPlayed
                  recentlyPlayedRep={recentlyPlayedRep}
                  containerRef={containerRef}
                  rowId="home-recently-played"
                />
              )}
              {inProgressRep.length > 0 && (
                <InProgress
                  inProgressRep={inProgressRep}
                  containerRef={containerRef}
                  rowId="home-in-progress"
                />
              )}
              {atYourLevelRep.length > 0 && (
                <AtYourLevel
                  atYourLevelRep={atYourLevelRep}
                  containerRef={containerRef}
                  rowId="home-at-your-level"
                />
              )}
              {morePracticeRep.length > 0 && (
                <MorePractice
                  morePracticeRep={morePracticeRep}
                  containerRef={containerRef}
                  rowId="home-more-practice"
                />
              )}
            </Box>
          ) : (
            <Box sx={{ position: 'relative' }}>
              <Box sx={{ position: 'absolute', left: '220px', top: '100px', textAlign: 'center' }}>
                <img style={{ width: '180px', marginBottom: '10px' }} src={EmptyRepertoire} />
                <Box sx={recTextStyles}>
                  Beat level 3 or higher<br></br>to unlock repertoire
                </Box>
              </Box>
            </Box>
          )}
          <Box sx={{ height: '20px' }} />
        </Box>
      </Box>
    </Box>
  );
};
export default Home;

interface SectionProps {
  containerRef: React.RefObject<HTMLDivElement | null>;
  rowId: string;
}

const RecentlyPlayed = ({
  recentlyPlayedRep,
  containerRef,
  rowId,
}: SectionProps & { recentlyPlayedRep: RepertoireData[] }) => {
  return (
    <Box sx={containerStyles}>
      <Box sx={titleStyles}>Recently Played</Box>
      <DragScroll rowId={rowId}>
        {recentlyPlayedRep.map((rep: RepertoireData, indx: number) => (
          <RepertoireItem repertoireData={rep} containerRef={containerRef} key={indx} />
        ))}
      </DragScroll>
    </Box>
  );
};

const AtYourLevel = ({
  atYourLevelRep,
  containerRef,
  rowId,
}: SectionProps & { atYourLevelRep: RepertoireData[] }) => {
  return (
    <Box sx={containerStyles}>
      <Box sx={titleStyles}>At Your Level</Box>
      <DragScroll rowId={rowId}>
        {atYourLevelRep.map((rep: RepertoireData, indx: number) => (
          <RepertoireItem repertoireData={rep} containerRef={containerRef} key={indx} />
        ))}
      </DragScroll>
    </Box>
  );
};

const InProgress = ({
  inProgressRep,
  containerRef,
  rowId,
}: SectionProps & { inProgressRep: RepertoireData[] }) => {
  return (
    <Box sx={containerStyles}>
      <Box sx={titleStyles}>In Progress</Box>
      <DragScroll rowId={rowId}>
        {inProgressRep.map((rep: RepertoireData, indx: number) => (
          <RepertoireItem repertoireData={rep} containerRef={containerRef} key={indx} />
        ))}
      </DragScroll>
    </Box>
  );
};

const MorePractice = ({
  morePracticeRep,
  containerRef,
  rowId,
}: SectionProps & { morePracticeRep: RepertoireData[] }) => {
  return (
    <Box sx={containerStyles}>
      <Box sx={titleStyles}>Need More Practice?</Box>
      <DragScroll rowId={rowId}>
        {morePracticeRep.map((rep: RepertoireData, indx: number) => (
          <RepertoireItem repertoireData={rep} containerRef={containerRef} key={indx} />
        ))}
      </DragScroll>
    </Box>
  );
};

const containerStyles = {
  margin: '4px 0px',
};

const titleStyles = {
  textAlign: 'left',
  fontFamily: 'Lato',
  color: '#ECECEC',
  fontStyle: 'normal',
  fontWeight: '700',
  fontSize: '15px',
  lineHeight: '24px',
};

const recForYouStyles = {
  padding: '24px 28px',
  backgroundColor: '#171F31',
};
const recTextStyles = {
  color: '#ADB1B7',
  textAlign: 'left',
  fontFamily: 'Lato',
  fontStyle: 'normal',
  fontWeight: '700',
  fontSize: '18px',
};
